import {memo} from 'react';
import type {ReactNode} from 'react';
import Eyebrow from '@/components/atoms/Eyebrow';
import {cx} from '@/util/cx';
import styles from './SectionHeader.module.scss';

interface SectionHeaderProps {
    /** A string becomes the standard Eyebrow; a node renders as-is. */
    eyebrow?: ReactNode;
    heading: ReactNode;
    /** Bold h3-styled line between the heading and the subheading. */
    boldSubheading?: ReactNode;
    subheading?: ReactNode;
    /** Subheading typography variant. Defaults to `subtitle1`. */
    subheadingVariant?: 'subtitle1' | 'body1';
    align?: 'center' | 'left';
    /** `wide`: the card-grid header — 1024px wide, 24px under the heading, 40px under the block. */
    layout?: 'standard' | 'wide';
    /** `large`: h1 at 35px, 48px from `sm`. `medium`: 32px / 40px, 48px / 56px from `md`. (Instead of the section heading scale.) */
    headingSize?: 'section' | 'medium' | 'large';
    /** `onDark`: white heading and subheading (and the on-dark Eyebrow) for dark sections. */
    tone?: 'default' | 'onDark';
    className?: string;
    headingClassName?: string;
    subheadingClassName?: string;
}

/** Plain stand-in for Common/SectionHeader: eyebrow + h1-styled <h2> + optional subtitle1 subheading. */
const SectionHeader = ({
    eyebrow,
    heading,
    boldSubheading,
    subheading,
    subheadingVariant = 'subtitle1',
    align = 'center',
    layout = 'standard',
    headingSize = 'section',
    tone = 'default',
    className,
    headingClassName,
    subheadingClassName
}: SectionHeaderProps) => (
    <div
        className={cx(
            styles.wrapper,
            layout === 'wide' && styles.wide,
            align === 'left' && styles.left,
            tone === 'onDark' && styles.onDark,
            className
        )}
    >
        {typeof eyebrow === 'string' ? <Eyebrow text={eyebrow} onDark={tone === 'onDark'} /> : eyebrow}
        <h2
            className={cx(
                'font-headers',
                styles.heading,
                headingSize === 'large' && styles.large,
                headingSize === 'medium' && styles.medium,
                headingClassName
            )}
        >
            {heading}
        </h2>
        {boldSubheading && <p className={`font-headers ${styles.boldSubheading}`}>{boldSubheading}</p>}
        {subheading !== undefined && subheading !== null && subheading !== '' && (
            <p className={cx('font-regular-text', styles[subheadingVariant], subheadingClassName)}>{subheading}</p>
        )}
    </div>
);

export default memo(SectionHeader);
